/* CATCHY EDITS THE TRACKERS — the screen half (Jay 2026-10-29, Max 2026-10-30; server: catchyWrite.js).

   ProposalCard     what Catchy is about to create or change, field by field old → new, with Confirm / Cancel. Nothing is
                    saved until Confirm; the server then re-reads the record and replays the screen's own request as the
                    person. Used under an answer in Catchy Chat (61_AiHub.jsx) and in the Ask Catchy bubble (AskBubble.jsx).
   ReminderPrefs    how often Catchy may start a conversation about the person's items with no update, and after how many
                    days; "show me now" previews today's message.
   RecordHistory    one SKU / project / sample round's history from the Activity Log: who, when, through what (screen or
                    Catchy, with the words they used), old → new. The chat itself opens only for the person who had it.

   Top-level components only — never declared inside another (a component made inside a component remounts on every
   render; GCC's emoji tray, 29/10). */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, History, Bot, Monitor, Cog, ExternalLink, Bell } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import './CatchyCard.css';

const call = async (method, path, body) => {
  const r = await fetch(`${BACKEND_URL}/api/catchy${path}`, { method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(j.error || `HTTP ${r.status}`); e.status = j.status; throw e; }
  return j;
};
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// a value as people read it: yyyy-mm-dd → dd/Mon/yyyy, lists joined, booleans as words
const shown = (v, t) => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ') || '—';
  if (typeof v === 'boolean') return v ? t('xCatchy.yes') : t('xCatchy.no');
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}/${MON[Number(m[2]) - 1]}/${m[1]}` : s;
};
const at = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : `${String(d.getDate()).padStart(2, '0')}/${MON[d.getMonth()]}/${d.getFullYear()} ${d.toTimeString().slice(0, 5)}`; };
const linkOf = (entity, id) => (!id ? null : entity === 'sample_round'
  ? `/development/sample-tracker?tab=sample&sample=${encodeURIComponent(id)}` : `/development/sample-tracker?tab=project&project=${encodeURIComponent(id)}`);

export function ProposalCard({ card, onChange }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const decide = async (what) => {
    setBusy(what); setErr('');
    try {
      const r = await call('POST', `/proposals/${card.id}/${what}`, {});
      onChange({ ...card, status: r.status, result: { id: r.id || card.entityId } });
    } catch (e) {
      setErr(e.message);
      if (e.status) onChange({ ...card, status: e.status, result: { reason: e.message } });
    } finally { setBusy(''); }
  };
  const open = card.status === 'proposed';
  const savedId = card.result?.id || card.entityId;
  return (
    <div className={`cc-card s-${card.status}`} data-cc-card={card.id}>
      <div className="cc-head">
        <span className="cc-kind">{card.kind === 'create' ? t('xCatchy.willCreate') : t('xCatchy.willChange')}</span>
        <b>{card.title}</b>
        <span className="cc-no">#{card.id}</span>
      </div>
      <table className="cc-table">
        <thead><tr><th>{t('xCatchy.field')}</th>{card.kind !== 'create' && <th>{t('xCatchy.now')}</th>}<th>{card.kind === 'create' ? t('xCatchy.value') : t('xCatchy.after')}</th></tr></thead>
        <tbody>
          {(card.changes || []).map((c) => (
            <tr key={c.key}>
              <td className="cc-f">{c.label}</td>
              {card.kind !== 'create' && <td className="cc-old">{c.derived ? '' : shown(c.oldShow ?? c.old, t)}</td>}
              <td className="cc-new">{shown(c.newShow ?? c.new, t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {card.note && <div className="cc-note">{card.note}</div>}
      {/* why this person may make the change (Jay task #1115) — the screen's own rule, named (server.js whyEditSku) */}
      {card.why && open && <div className="cc-why" data-cc-why={card.why}>{t(`xCatchy.why_${card.why}`, { defaultValue: '' })}</div>}
      {open ? (
        <div className="cc-btns">
          <span className="cc-hint">{t('xCatchy.notSaved')}</span>
          <button type="button" className="pa-btn" onClick={() => decide('cancel')} disabled={!!busy} data-cc-cancel={card.id}><X size={14} /> {t('xCatchy.cancel')}</button>
          <button type="button" className="pa-btn primary" onClick={() => decide('confirm')} disabled={!!busy} data-cc-confirm={card.id}>
            <Check size={14} /> {busy === 'confirm' ? t('xCatchy.saving') : t('xCatchy.confirm')}
          </button>
        </div>
      ) : (
        <div className={`cc-state st-${card.status}`} data-cc-state={card.status}>
          {card.status === 'applied' && <>{t('xCatchy.applied')} {linkOf(card.entity, savedId) && <a href={linkOf(card.entity, savedId)}>{savedId} <ExternalLink size={12} /></a>}</>}
          {card.status === 'cancelled' && t('xCatchy.cancelled')}
          {card.status === 'stale' && t('xCatchy.stale')}
          {card.status === 'failed' && t('xCatchy.failed')}
          {card.status === 'applying' && t('xCatchy.saving')}
          {card.result?.reason && card.status !== 'applied' && <div className="cc-reason">{card.result.reason}</div>}
        </div>
      )}
      {err && open && <div className="cc-reason">{err}</div>}
    </div>
  );
}

export function ReminderPrefs() {
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(null);
  useEffect(() => { call('GET', '/prefs').then(setP).catch(() => setP(null)); }, []);
  const save = async (patch) => {
    try { const r = await call('PUT', '/prefs', patch); setP(r); setMsg(t('xCatchy.prefSaved')); setTimeout(() => setMsg(''), 2500); } catch (e) { setMsg(e.message); }
  };
  const look = async () => {
    try { const r = await call('POST', '/nudge/me', { send: false }); setPreview(r.none ? t('xCatchy.nothingToRemind') : r.preview); } catch (e) { setPreview(e.message); }
  };
  if (!p) return null;
  return (
    <div className="cc-prefs" data-cc-prefs>
      <Bell size={14} />
      <span>{t('xCatchy.remind')}</span>
      <select value={p.nudge} onChange={(e) => save({ nudge: e.target.value })} data-cc-nudge>
        {['daily', '2days', 'weekly', 'off'].map((k) => <option key={k} value={k}>{t(`xCatchy.n_${k}`)}</option>)}
      </select>
      {p.nudge !== 'off' && (
        <>
          <select value={p.staleDays} onChange={(e) => save({ staleDays: Number(e.target.value) })} data-cc-days>
            {[3, 5, 7, 10, 14].map((d) => <option key={d} value={d}>{t('xCatchy.staleOpt', { n: d })}</option>)}
          </select>
          <button type="button" className="cc-link" onClick={look} data-cc-preview>{t('xCatchy.preview')}</button>
        </>
      )}
      {msg && <em className="cc-ok">{msg}</em>}
      {preview !== null && (
        <div className="cc-preview" role="dialog">
          <button type="button" className="cc-x" onClick={() => setPreview(null)} aria-label={t('xCatchy.close')}><X size={14} /></button>
          <pre>{preview}</pre>
        </div>
      )}
    </div>
  );
}

/* the history drawer of one record — opened from the SKU, project and round forms */
export function RecordHistory({ entity, id, onClose }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    call('GET', `/history?entity=${encodeURIComponent(entity)}&id=${encodeURIComponent(id)}`).then((r) => setRows(r.rows)).catch((e) => setErr(e.message));
  }, [entity, id]);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  }, [onClose]);
  return (
    <div className="cc-hist-back" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cc-hist" role="dialog" aria-modal="true" data-cc-history={id}>
        <div className="cc-hist-head"><History size={16} /> <b>{t('xCatchy.historyOf', { id })}</b>
          <button type="button" className="cc-x" onClick={onClose} aria-label={t('xCatchy.close')}><X size={16} /></button></div>
        {err && <div className="cc-reason">{err}</div>}
        {!rows && !err && <p className="cc-muted">{t('xCatchy.loading')}</p>}
        {rows && !rows.length && <p className="cc-muted">{t('xCatchy.noHistory')}</p>}
        {rows && rows.length > 0 && (
          <div className="cc-hist-list">
            {rows.map((r) => (
              <div key={r.id} className="cc-hrow" data-cc-hrow={r.id}>
                <div className="cc-hmeta">
                  <span className="cc-hwhen">{at(r.ts)}</span>
                  <b>{r.actor || '—'}</b>
                  {r.via === 'Catchy' ? <span className="cc-door d-catchy"><Bot size={11} /> Catchy</span>
                    : r.via === 'System' ? <span className="cc-door d-system"><Cog size={11} /> {t('xCatchy.viaSystem')}</span>
                      : r.via ? <span className="cc-door d-screen"><Monitor size={11} /> {t('xCatchy.viaScreen')}</span> : null}
                  {r.entityId !== id && <span className="cc-hent">{r.entityId}</span>}
                </div>
                {r.said && (
                  <div className="cc-said">“{r.said}”{r.convPk && <a href={`/ai-hub?tab=ask&conv=${r.convPk}`}> {t('xCatchy.openChat')} <ExternalLink size={11} /></a>}</div>
                )}
                <div className="cc-hchange">
                  {r.field ? <><span className="cc-f">{r.field}</span> <span className="cc-old">{shown(r.oldValue, t)}</span> → <span className="cc-new">{shown(r.newValue, t)}</span></>
                    : <span>{r.detail || r.action}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
